import React, { Component } from "react";
import styled, { css } from "styled-components";

import { Icon, IconButton, Tooltip } from "@material-ui/core";
import PresentationLibraryPaneController, { PresentationLibraryPaneControllerState, PresentationLibraryPresentation } from "js/controllers/PresentationLibraryPaneController";
import { FolderLocationBreadcrumb } from "js/editor/PresentationLibrary/components/FolderLocationBreadcrumb";
import { TruncatedTooltip } from "js/editor/PresentationLibrary/components/TruncatedTooltip";
import { IWorkspaceResource } from "common/interfaces";
import { IWorkspaceResourceFolder } from "common/interfaces";
import getLogger from "js/core/logger";
import { getPresentation } from "js/core/models/presentation";
import { getPresentationContextMenuItems } from "js/editor/PresentationLibrary/PresentationContextMenu";
import AvatarStack, { AvatarStackProfile, getMaxVisibleAvatarsForWidth } from "js/editor/PresentationLibrary/components/AvatarStack";
import { fetchPresentationCollaboratorProfiles } from "js/editor/PresentationLibrary/utils/fetchPresentationCollaboratorProfiles";
import { isCompactLibrary } from "js/editor/PresentationLibrary/utils/isCompactLibrary";
import { app } from "js/namespaces";
import { ShowDialog } from "js/react/components/Dialogs/BaseDialog";
import { IconMenu } from "js/react/components/UiComponents";
import { thumbnailMenuButtonStyles } from "js/react/views/AddSlide/Panes/Components/ThumbnailCard";
import {
    MultiSelectThumbnailGrid,
    Thumbnail,
    ThumbnailContainer
} from "js/react/views/AddSlide/Panes/Components/ThumbnailGrid";
import { ShareDialog } from "js/react/views/PresentationSettings/dialogs/ShareDialog";
import { _ } from "js/vendor";
import ReadOnlyBadge from "./components/ReadOnlyBadge";
import { getLastAccessedLabel, LastActivityTooltipStyles } from "js/editor/PresentationLibrary/utils/getLastAccessedLabel";
import { getCjkUiFontFamilyCss } from "common/fontConstants";
import WorkspaceController from "js/controllers/WorkspaceController";

const logger = getLogger();

const GRID_MIN_TITLE_WIDTH = 165;
const GRID_MIN_VISIBLE_AVATARS = 2;
const GRID_MAX_VISIBLE_AVATARS = 5;
const GRID_PROFILE_REQUESTS = GRID_MAX_VISIBLE_AVATARS + 1;

const ThumbnailGridContainer = styled.div`
    margin: 0px 0px 20px;
    overflow: hidden;

    .dark-mode & {
        background: #333;
    }
`;

type PresentationLibraryGridProps = PresentationLibraryPaneControllerState & {
    onSelectedPresentations: (presentations: PresentationLibraryPresentation[]) => void;
    onDoubleClick: (presentation: PresentationLibraryPresentation) => void;
    onStartDragItems: (items: PresentationLibraryPresentation[]) => void;
    onDropDragItems: (items: PresentationLibraryPresentation[]) => void;
    readOnly: boolean;
    resourceFolders: IWorkspaceResourceFolder[];
    onNavigateToFolder: (folderId: string, subFolderId?: string) => void;
}

export const PresentationLibraryGrid = WorkspaceController.withState(PresentationLibraryPaneController.withState(
    class PresentationLibraryGrid extends Component<PresentationLibraryGridProps> {
        handleThumbnailVisibilityChange = (visible: boolean, presentationId: string) => {
            const { data } = this.props;

            if (!visible) {
                return;
            }

            if (!PresentationLibraryPaneController.canLoadMore) {
                return;
            }

            const index = data.findIndex(({ document }) => document.id === presentationId);
            if (index === data.length - 1) {
                PresentationLibraryPaneController.loadMore();
            }
        }

        render() {
            const { onSelectedPresentations, onDoubleClick, onStartDragItems, onDropDragItems, readOnly, resourceFolders, onNavigateToFolder, fetching, data } = this.props;

            const presentations = PresentationLibraryPaneController.legacyData;
            const resources = data.map(d => d.resource);

            return (
                <ThumbnailGridContainer>
                    <LastActivityTooltipStyles />
                    <MultiSelectThumbnailGrid
                        items={presentations}
                        responsiveColumns
                        colGap={24}
                        rowGap={24}
                        readOnly={readOnly}
                        allowDragging={!readOnly}
                        allowMultiSelect={!readOnly}
                        thumbnailClass={PresentationLibraryPreviewThumbnail}
                        thumbnailProps={{
                            resourceFolders: resourceFolders ?? [],
                            resources,
                            onNavigateToFolder: onNavigateToFolder ?? (() => { }),
                            onVisibleChange: this.handleThumbnailVisibilityChange
                        }}
                        onItemSelected={items => onSelectedPresentations(items)}
                        onDoubleClick={onDoubleClick}
                        onStartDragItems={onStartDragItems}
                        onDropDragItems={onDropDragItems}
                        renderContextMenuItems={app.isMobileOrTablet ? undefined : selectedPresentations => getPresentationContextMenuItems({ selectedPresentations })}
                        isLoadingMore={fetching}
                    />
                </ThumbnailGridContainer>
            );
        }
    }));

const ThumbnailBar = styled.div`
    display: flex;
    align-items: center;
    gap: 10px;
    height: 20px; 
    label {
        font-size: 14px;
        line-height: 18px;
        letter-spacing: 0px;
        color: #444;
        font-family: "Source Sans Pro", ${getCjkUiFontFamilyCss()};
        font-weight: 600;
        white-space: nowrap;
        text-overflow: ellipsis;
        overflow: hidden;
        text-align: left;
        flex: 1;
        min-width: 0;
        text-transform: none;
        margin: 0px;
    }
    button {
        padding: 2px;
    }

    .dark-mode & {
        label {
            color: #ddd;
        }
    }
`;

const AvatarStackWrap = styled.div`
    flex-shrink: 0;
    display: flex;
    align-items: center;
`;

const ThumbnailContent = styled.div`
    display: flex;
    flex-direction: column;
    gap: 2px;
    width: 100%;
    padding: 0;
    margin-top: 4px;
`;

const LastAccessedLabel = styled.div`
    white-space: nowrap;
`;

const TruncatedText = styled.div`
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
`;

const ThumbnailMetaRow = styled.div`
    display: flex;
    align-items: center;
    font-size: 11px;
    line-height: 16px;
    letter-spacing: 0px;
    min-height: 20px;
    color: #666;
    min-width: 0;
`;

const PresentationThumbnailSelected = css`
    border: none;
    background: #D5E6ED;

    :hover {
        cursor: default;
        background: #D5E6ED;
    }
`;

const PresentationThumbnail = css`
    border: none;
    background: #FFF;

    :hover {
        cursor: default;
        background: #F8FAFB;
        box-shadow: inset 0 0 0 1px #DDE3E8;
    }
`;

const PresentationThumbnailMobile = css`
    border: none;
    background: #FFF;

    :active {
        background: #F8FAFB;
    }
`;

const PresentationThumbnailContainer = styled(ThumbnailContainer) <{ selected: boolean }>`
    position: relative;
    display: flex;
    flex-direction: column;
    align-items: stretch;
    justify-content: flex-start;
    gap: 12px;
    padding: 15px 15px 12px;
    border-radius: 4px;
    overflow: hidden;

    ${props => isCompactLibrary()
        ? PresentationThumbnailMobile
        : props.selected
            ? PresentationThumbnailSelected
            : PresentationThumbnail}
`;

const ThumbnailMenuWrap = styled.div`
    position: absolute;
    right: 6px;
    bottom: 6px;
    border-radius: 4px;
    cursor: pointer;
    // spinner is 1000, so we need to be above it
    z-index: 1001;
`;

const StyledIconMenu = styled(IconMenu)`
    &&& {
        ${thumbnailMenuButtonStyles}
        background-color: #111827E6;
        border: 1px solid rgba(255, 255, 255, 0.28);
        border-radius: 50%;
        box-shadow: 0 1px 2px rgba(0, 0, 0, 0.18);
        color: white;
        width: 28px;
        height: 28px;
        transition: background-color 0.15s ease, transform 0.15s ease, border-color 0.15s ease;

        &:hover {
            background-color: #111827;
            border-color: rgba(255, 255, 255, 0.42);
            transform: scale(1.04);
        }

        .MuiIcon-root {
            color: white;
        }
    }
`;

const ReadOnlyBadgeContainer = styled.div`
    position:absolute;
    top: 8px;
    right: 8px;
    z-index: 100;
    align-items: center;
    justify-content: center;
    
`;

const PlaceholderContainer = styled.div`
    width: 100%;
`;

const PlaceholderImage = styled.div`
    width: 100%;
    aspect-ratio: 16/9;
`;

const PlaceholderLabel = styled.div`
    width: 100%;
    height: 24px;
    margin-top: 20px;
`;

const ThumbnailImageWrap = styled.div`
    position: relative;
    border-radius: 2px;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.12);
    overflow: hidden;
    transition: box-shadow 0.2s ease;

    .thumbnail,
    img {
        border-radius: 2px;
    }

    ${() => !isCompactLibrary() && css`${PresentationThumbnailContainer}:hover & {
        box-shadow: 0 2px 5px rgba(0, 0, 0, 0.16);
    }`}
`;

interface PresentationLibraryPreviewThumbnailProps {
    item: PresentationLibraryPresentation;
    onMouseDown: (event: React.MouseEvent<HTMLDivElement>) => void;
    onMouseUp?: (event: React.MouseEvent<HTMLDivElement>) => void;
    onDoubleClick: (event: React.MouseEvent<HTMLDivElement>) => void;
    onContextMenu: (event: React.MouseEvent<HTMLDivElement>) => void;
    selected: boolean;
    readOnly: boolean;
    onMenuClick?: (item: PresentationLibraryPresentation) => void;
    resourceFolders?: IWorkspaceResourceFolder[];
    resources?: IWorkspaceResource[];
    onNavigateToFolder?: (folderId: string, subFolderId?: string) => void;
    onVisibleChange?: (visible: boolean, presentationId: string) => void;
}

interface PresentationLibraryPreviewThumbnailState {
    thumbnailUrl: string;
    visible: boolean;
    collaboratorProfiles: AvatarStackProfile[];
    isHovering: boolean;
    cardWidth: number | null;
}

export class PresentationLibraryPreviewThumbnail extends Component<PresentationLibraryPreviewThumbnailProps, PresentationLibraryPreviewThumbnailState> {
    ref: React.RefObject<HTMLDivElement>;
    observer: IntersectionObserver;
    resizeObserver?: ResizeObserver;
    isFetchingCollaboratorProfiles = false;

    constructor(props: PresentationLibraryPreviewThumbnailProps) {
        super(props);

        this.state = {
            thumbnailUrl: null,
            visible: false,
            collaboratorProfiles: [],
            isHovering: false,
            cardWidth: null,
        };

        this.ref = React.createRef();
    }

    fetchThumbnailUrl() {
        const { item: { getThumbnailUrl } } = this.props;
        const { thumbnailUrl: currentThumbnailUrl } = this.state;

        this.setState({ thumbnailUrl: null });

        getThumbnailUrl()
            .then(thumbnailUrl => this.setState({ thumbnailUrl }))
            .catch(err => {
                logger.error(err, "[PresentationLibraryPreviewThumbnail] presentationModel.getThumbnailUrl() failed");

                if (currentThumbnailUrl) {
                    // Restore previous thumbnail
                    this.setState({ thumbnailUrl: currentThumbnailUrl });
                }
            });
    }

    async fetchCollaboratorProfiles() {
        if (this.isFetchingCollaboratorProfiles || this.state.collaboratorProfiles.length > 0) {
            return;
        }

        const { item } = this.props;
        this.isFetchingCollaboratorProfiles = true;
        try {
            const profiles = await fetchPresentationCollaboratorProfiles({
                workspaceId: item.workspaceId,
                resourceId: item.id,
                ownerUid: item.ownerUid,
                maxProfileRequests: GRID_PROFILE_REQUESTS,
            });
            if (this.ref.current) this.setState({ collaboratorProfiles: profiles });
        } catch (err) {
            logger.error(err, "[PresentationLibraryPreviewThumbnail] fetchCollaboratorProfiles failed");
        } finally {
            this.isFetchingCollaboratorProfiles = false;
        }
    }

    componentDidMount() {
        this.observer = new IntersectionObserver(entries => {
            if (entries[0].isIntersecting === true) {
                if (_.isEmpty(this.state.thumbnailUrl)) {
                    this.fetchThumbnailUrl();
                }
                this.fetchCollaboratorProfiles();
                if (!this.state.visible) {
                    this.setState({ visible: true });
                }
            } else {
                if (this.state.visible) {
                    this.setState({ visible: false });
                }
            }
        });

        this.observer.observe(this.ref.current);

        this.resizeObserver = new ResizeObserver(entries => {
            const width = entries[0]?.contentRect.width;
            if (width) {
                this.setState({ cardWidth: width });
            }
        });
        this.resizeObserver.observe(this.ref.current);
    }

    componentWillUnmount() {
        this.observer.disconnect();
        this.resizeObserver?.disconnect();
    }

    componentDidUpdate(prevProps: PresentationLibraryPreviewThumbnailProps, prevState: PresentationLibraryPreviewThumbnailState) {
        if (prevProps.item.firstSlideModifiedAt !== this.props.item.firstSlideModifiedAt || prevProps.item.slideIds[0] !== this.props.item.slideIds[0]) {
            this.fetchThumbnailUrl();
        }
        if (!prevState.visible && this.state.visible && this.state.collaboratorProfiles.length === 0) {
            this.fetchCollaboratorProfiles();
        }

        if (prevState.visible !== this.state.visible) {
            this.props.onVisibleChange?.(this.state.visible, this.props.item.id);
        }
    }

    handleSharePresentation = async () => {
        const { item } = this.props;

        const presentation = await getPresentation(item.id, { permission: "write", autoSync: true });
        ShowDialog(ShareDialog, { presentation, onClose: () => presentation.disconnect() });
    }

    render() {
        const { item, onMouseDown, onMouseUp, onDoubleClick, onContextMenu, selected, readOnly, resourceFolders, onNavigateToFolder, onMenuClick, resources } = this.props;
        const { thumbnailUrl, visible, collaboratorProfiles, isHovering, cardWidth } = this.state;
        const lastAccessedLabel = getLastAccessedLabel(item, true);
        const shouldShowOwnerAvatar = !item.lastActivity?.createdAt;
        // Synthetic owner row until collaborator fetch returns (avoids empty avatar on first paint)
        const avatarProfiles = shouldShowOwnerAvatar && collaboratorProfiles.length === 0
            ? [{
                displayName: item.ownerName,
                email: item.ownerUid,
                photoURL: item.ownerPhotoURL,
                isCreator: true,
            }]
            : collaboratorProfiles;
        // AvatarStack deliberately hides a lone profile unless showSingleAvatar; after an edit we still often have only the owner in the stack
        const showSingleAvatarInGrid = avatarProfiles.length === 1;
        const maxVisibleAvatars = getMaxVisibleAvatarsForWidth(
            cardWidth ? cardWidth - GRID_MIN_TITLE_WIDTH : null,
            GRID_MIN_VISIBLE_AVATARS,
            GRID_MAX_VISIBLE_AVATARS
        );

        const resource = resources.find(r => r.resourceId === item.id);

        return (
            <PresentationThumbnailContainer
                selected={selected}
                data-item-id={item.id}
                className="thumbnail-grid-item"
                onMouseDown={onMouseDown}
                onMouseUp={onMouseUp}
                onDoubleClick={onDoubleClick}
                onContextMenu={event => {
                    if (app.isMobileOrTablet) {
                        event.preventDefault();
                        event.stopPropagation();
                        return;
                    }
                    onContextMenu(event);
                }}
                onMouseEnter={() => this.setState({ isHovering: true })}
                onMouseLeave={() => this.setState({ isHovering: false })}
                ref={this.ref}
            >
                {!visible && <PlaceholderContainer><PlaceholderImage /><PlaceholderLabel /></PlaceholderContainer>}
                {visible && <>
                    <ThumbnailImageWrap>
                        <Thumbnail
                            // used to prevent the box-shadow effect from being applied to the thumbnail
                            readOnly={true}
                            url={item.dirty ? null : thumbnailUrl}
                            showSpinner={true}
                        >
                            {(!item.permissions.owner && !item.permissions.write) && <ReadOnlyBadgeContainer><ReadOnlyBadge /></ReadOnlyBadgeContainer>}
                        </Thumbnail>
                        {isHovering && !isCompactLibrary() && !readOnly && (
                            <ThumbnailMenuWrap
                                onMouseDown={e => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                }}
                                onMouseUp={e => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                }}
                                onDoubleClick={e => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                }}
                                onClick={e => {
                                    e.stopPropagation();
                                }}
                            >
                                <StyledIconMenu
                                    handleCloseMenu={() => this.setState({ isHovering: false })}
                                    onClickCapture={() => onMenuClick?.(item)}
                                >
                                    {getPresentationContextMenuItems({
                                        selectedPresentations: [item],
                                    })}
                                </StyledIconMenu>
                            </ThumbnailMenuWrap>
                        )}
                    </ThumbnailImageWrap>
                    <ThumbnailContent>
                        <ThumbnailBar>
                            <TruncatedTooltip title={item.name}>
                                <label className="presentation_item_name">{item.name}</label>
                            </TruncatedTooltip>
                            <AvatarStackWrap>
                                <AvatarStack
                                    profiles={avatarProfiles}
                                    showSingleAvatar={showSingleAvatarInGrid}
                                    minVisibleAvatars={GRID_MIN_VISIBLE_AVATARS}
                                    maxVisibleAvatars={maxVisibleAvatars}
                                />
                            </AvatarStackWrap>
                            {
                                !window.isZoomApp &&
                                isCompactLibrary() &&
                                !readOnly &&
                                item.permissions.write &&
                                <IconButton
                                    className="text-blue"
                                    onMouseDown={event => {
                                        event.preventDefault();
                                        event.stopPropagation();
                                    }}
                                    onMouseUp={event => {
                                        event.preventDefault();
                                        event.stopPropagation();
                                    }}
                                    onDoubleClick={event => {
                                        event.preventDefault();
                                        event.stopPropagation();
                                    }}
                                    onContextMenu={event => {
                                        event.preventDefault();
                                        event.stopPropagation();
                                    }}
                                    onClick={event => {
                                        event.preventDefault();
                                        event.stopPropagation();
                                        this.handleSharePresentation();
                                    }}
                                >
                                    <Icon>share</Icon>
                                </IconButton>
                            }
                        </ThumbnailBar>
                        <ThumbnailMetaRow>
                            {lastAccessedLabel && <>
                                <LastAccessedLabel>
                                    <Tooltip
                                        title={`${lastAccessedLabel?.actionLabel ?? ""} by ${lastAccessedLabel?.userName ?? ""}`}
                                        classes={{ tooltip: "last-activity-tooltip" }}
                                        open={lastAccessedLabel?.userName === "you" ? false : undefined}
                                    >
                                        <TruncatedText>
                                            {lastAccessedLabel?.label ?? ""}
                                        </TruncatedText>
                                    </Tooltip>
                                </LastAccessedLabel>
                            </>}

                            {resourceFolders && resource && onNavigateToFolder && (
                                <FolderLocationBreadcrumb
                                    showDivider={!!lastAccessedLabel}
                                    isGrid
                                    resource={resource}
                                    resourceFolders={resourceFolders}
                                    onNavigateToFolder={onNavigateToFolder}
                                />
                            )}
                        </ThumbnailMetaRow>
                    </ThumbnailContent>
                </>}
            </PresentationThumbnailContainer>
        );
    }
}
